import { useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { adminLocationsQuery } from '@/queries';

/**
 * Move stock from one bay to another. T048, FR-111.
 *
 * This is deliberately NOT two adjustments. A transfer is one event: the tub
 * leaves bay A and arrives in bay B, and if the second half fails the stock is
 * in neither place. The route behind this writes both movements inside one
 * database transaction, so the ledger can never hold half of a move.
 *
 * The note is required, and the server requires it too. A tub that moved for
 * no recorded reason is a tub somebody will look for in the old bay.
 */
export function MoveStock({ itemId, uom }: { itemId: string; uom: string }) {
  const qc = useQueryClient();
  const { locations } = useSuspenseQuery(adminLocationsQuery).data;

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [qty, setQty] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/stock/transfer', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          item_id: itemId,
          from_location_id: from,
          to_location_id: to,
          qty: Number(qty),
          note: note.trim(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      setDone('Moved. Both halves are in the history below, under the same note.');
      setQty('');
      setNote('');
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'locations'] });
    },
    onError: (e: Error) => {
      setDone(null);
      setError(e.message);
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-[10px] border border-line px-3 py-2 text-[14px]"
      >
        Move to another bay
      </button>
    );
  }

  const valid = from && to && from !== to && Number(qty) > 0 && note.trim() !== '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) submit.mutate();
      }}
      className="rounded-[10px] border border-line bg-panel p-4"
    >
      <p className="mb-3 text-[15px] font-semibold">Move to another bay</p>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-[13px]">
          Out of
          <select
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
          >
            <option value="">Choose a bay</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id} disabled={Boolean(l.locked_by_take_id)}>
                {l.code} {l.label}
                {l.locked_by_take_id ? ' — being counted' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="text-[13px]">
          Into
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
          >
            <option value="">Choose a bay</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id} disabled={Boolean(l.locked_by_take_id) || l.id === from}>
                {l.code} {l.label}
                {l.locked_by_take_id ? ' — being counted' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="text-[13px]">
          How much, in {uom}
          <input
            type="number"
            step="any"
            min="0"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="12"
            className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
          />
        </label>
      </div>

      <label className="mt-3 block text-[13px]">
        Why it moved
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Re-racking bay 2"
          className="mt-1 h-10 w-full rounded-[10px] border border-line bg-panel px-2 text-[14px]"
        />
      </label>

      {error && (
        <p className="mt-3 rounded-[10px] border border-line p-3 text-[14px]">{error}</p>
      )}
      {done && <p className="mt-3 text-[14px] text-muted">{done}</p>}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="submit"
          disabled={!valid || submit.isPending}
          className="rounded-[10px] border border-line-strong px-3 py-2 text-[14px] font-semibold disabled:opacity-50"
        >
          {submit.isPending ? 'Moving…' : 'Move it'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[14px] text-muted"
        >
          Close
        </button>
      </div>
    </form>
  );
}
